/**
 * CrewFeedPage ✦ — TikTok/Bumble swipe feed for Crew users
 *
 * Vertical, mobile-first deck of fullscreen opportunity cards.
 * Drag right → Interested ❤️ (instant apply, no CV)
 * Drag left  → Skip
 * Center button → More Info (expands description + tags inline)
 *
 * Uses framer-motion for the swipe physics + tilt feel. The card
 * stack underneath is staggered behind the current one so each
 * decision reveals fresh energy beneath.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, useMotionValue, useTransform, AnimatePresence } from 'framer-motion';
import {
  Heart, X as XIcon, Info, Lightning, UsersThree, ArrowLeft,
  BookmarkSimple, UserPlus, ChatCircleDots,
} from '@phosphor-icons/react';
import CrewVideoPlayer from '../../components/crew/CrewVideoPlayer';
import CrewPushOptIn from '../../components/crew/CrewPushOptIn';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BG = '#07071A';
const PANEL = 'rgba(255,255,255,0.05)';
const PANEL_BORDER = 'rgba(255,255,255,0.1)';
const PURPLE = '#6C3BFF';
const PINK = '#FF3CAC';
const BLUE = '#33C3FF';
const TEXT = '#FFFFFF';
const TEXT_MUTED = '#B8B8D1';
const FONT_STACK = `'Inter', 'Poppins', -apple-system, BlinkMacSystemFont, system-ui, sans-serif`;

const TAG_DISPLAY = {
  urgent: { label: 'URGENT', color: PINK },
  today: { label: 'TODAY', color: PINK },
  beginner_friendly: { label: 'BEGINNER FRIENDLY', color: BLUE },
  bring_friend: { label: 'BRING A FRIEND', color: PURPLE },
  trial_shift: { label: 'TRIAL SHIFT', color: BLUE },
  ongoing_role: { label: 'ONGOING ROLE', color: PURPLE },
};

const OpportunityCard = ({ opportunity, onSwipe, onSave, onToggleBringFriend, onRespondDirected, isTop, stackIndex, bringFriend }) => {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-300, 0, 300], [-22, 0, 22]);
  const opacity = useTransform(x, [-300, -150, 0, 150, 300], [0.4, 0.9, 1, 0.9, 0.4]);
  const likeGlow = useTransform(x, [0, 150], [0, 1]);
  const skipGlow = useTransform(x, [-150, 0], [1, 0]);
  const [showInfo, setShowInfo] = useState(false);
  const [savedLocal, setSavedLocal] = useState(Boolean(opportunity.is_saved));
  const [directedBusy, setDirectedBusy] = useState('');
  const tags = opportunity.tags || [];
  const heroBg = opportunity.photo_url
    ? `url(${opportunity.photo_url})`
    : `linear-gradient(135deg, ${PURPLE} 0%, ${PINK} 50%, ${BLUE} 100%)`;
  const isUrgent = tags.includes('urgent') || tags.includes('today');
  const allowsBringFriend = tags.includes('bring_friend');
  const directed = opportunity.directed_invite || null;
  const GOLD = '#FFD166';

  const handleDragEnd = (_, info) => {
    if (info.offset.x > 120) onSwipe('interested');
    else if (info.offset.x < -120) onSwipe('skip');
  };

  const handleSaveTap = (e) => {
    e.stopPropagation();
    const next = !savedLocal;
    setSavedLocal(next);
    if (onSave) onSave(opportunity.id, next);
  };

  const respondDirected = async (response) => {
    if (!directed || directedBusy) return;
    setDirectedBusy(response);
    try {
      await onRespondDirected?.(directed.id, response, opportunity.id);
    } finally {
      setDirectedBusy('');
    }
  };

  return (
    <motion.div
      drag={isTop ? 'x' : false}
      dragConstraints={{ left: 0, right: 0 }}
      onDragEnd={handleDragEnd}
      style={{
        x, rotate, opacity,
        position: 'absolute', inset: 0,
        zIndex: 10 - stackIndex,
        scale: 1 - stackIndex * 0.04,
        y: stackIndex * 8,
        transformOrigin: 'center bottom',
      }}
      animate={!isTop ? { scale: 1 - stackIndex * 0.04, y: stackIndex * 8 } : undefined}
      transition={{ type: 'spring', stiffness: 200, damping: 24 }}
      data-testid={`opportunity-card-${opportunity.id}`}
    >
      <div style={{
        height: '100%', borderRadius: 28, overflow: 'hidden',
        background: heroBg,
        backgroundSize: 'cover', backgroundPosition: 'center',
        position: 'relative',
        boxShadow: directed
          ? `0 20px 56px rgba(0,0,0,0.55), 0 0 0 2px ${GOLD}AA, 0 0 36px ${GOLD}55`
          : '0 18px 50px rgba(0,0,0,0.55)',
        border: `1px solid ${directed ? GOLD : PANEL_BORDER}`,
      }}>
        {/* Vibe video — autoplays only when this is the TOP card on
            screen, so phones never decode 3 streams in parallel.
            Falls back to the photo background if no clip. */}
        {opportunity.video_url && (
          <div style={{
            position: 'absolute', inset: 0, pointerEvents: isTop ? 'auto' : 'none',
          }}>
            <CrewVideoPlayer
              src={isTop ? opportunity.video_url : ''}
              poster={opportunity.video_poster_url || opportunity.photo_url || ''}
              targetKind="opportunity"
              targetId={opportunity.id}
              rounded={28}
              testId={`opportunity-video-${opportunity.id}`}
            />
          </div>
        )}

        {/* Dark gradient overlay (top fade + bottom legibility) */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(180deg, rgba(7,7,26,0.4) 0%, transparent 30%, rgba(7,7,26,0.92) 75%, rgba(7,7,26,0.98) 100%)',
        }} />

        {/* Like / Skip glow overlays follow the drag */}
        <motion.div style={{
          opacity: likeGlow, position: 'absolute', inset: 0, pointerEvents: 'none',
          background: `radial-gradient(circle at 70% 50%, ${PINK}55, transparent 55%)`,
        }} />
        <motion.div style={{
          opacity: skipGlow, position: 'absolute', inset: 0, pointerEvents: 'none',
          background: `radial-gradient(circle at 30% 50%, ${TEXT_MUTED}33, transparent 55%)`,
        }} />

        {/* Top tags */}
        <div style={{
          position: 'absolute', top: 20, left: 20, right: 60,
          display: 'flex', flexWrap: 'wrap', gap: 6,
        }}>
          {directed && (
            <span
              data-testid={`directed-ribbon-${opportunity.id}`}
              style={{
                fontFamily: FONT_STACK, fontSize: 10.5, fontWeight: 900,
                letterSpacing: '0.18em', padding: '6px 12px', borderRadius: 999,
                background: `linear-gradient(135deg, ${GOLD}DD, #FFB347 80%)`,
                color: '#1A1100',
                border: `1px solid ${GOLD}`,
                boxShadow: `0 0 26px ${GOLD}AA, 0 0 0 1px ${GOLD}AA inset`,
                animation: 'crewPulse 2.2s ease-in-out infinite',
                whiteSpace: 'nowrap',
              }}
            >
              ✨ {(directed.supplier_name || 'A supplier').split(' ')[0]} REQUESTED YOU ✦
            </span>
          )}
          {tags.slice(0, 4).map((t) => {
            const td = TAG_DISPLAY[t] || { label: t.replace(/_/g, ' ').toUpperCase(), color: PURPLE };
            return (
              <span key={t} style={{
                fontFamily: FONT_STACK, fontSize: 10, fontWeight: 800,
                letterSpacing: '0.12em', padding: '5px 10px', borderRadius: 999,
                background: `${td.color}33`, color: TEXT,
                border: `1px solid ${td.color}88`, backdropFilter: 'blur(8px)',
                boxShadow: isUrgent && (t === 'urgent' || t === 'today')
                  ? `0 0 18px ${td.color}88, 0 0 0 1px ${td.color}aa inset`
                  : 'none',
                animation: isUrgent && (t === 'urgent' || t === 'today') ? 'crewPulse 1.8s ease-in-out infinite' : undefined,
              }}>
                {td.label}
              </span>
            );
          })}
        </div>

        {/* Bookmark — top-right, save opportunity for later */}
        {isTop && (
          <button
            onClick={handleSaveTap}
            onPointerDown={(e) => e.stopPropagation()}
            data-testid={`card-save-${opportunity.id}`}
            aria-label={savedLocal ? 'Unsave' : 'Save'}
            style={{
              position: 'absolute', top: 18, right: 16,
              width: 36, height: 36, borderRadius: '50%',
              background: savedLocal ? `linear-gradient(135deg, ${PINK}, ${PURPLE})` : 'rgba(0,0,0,0.45)',
              border: savedLocal ? 'none' : `1px solid ${PANEL_BORDER}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', backdropFilter: 'blur(8px)',
              boxShadow: savedLocal ? `0 0 16px ${PINK}66` : 'none',
              zIndex: 5,
            }}
          >
            <BookmarkSimple size={16} weight={savedLocal ? 'fill' : 'bold'} color={TEXT} />
          </button>
        )}

        {/* Main info — bottom slab */}
        <div style={{
          position: 'absolute', left: 24, right: 24, bottom: 24,
        }}>
          <p style={{
            margin: 0, fontFamily: FONT_STACK, fontWeight: 800, fontSize: 12,
            letterSpacing: '0.1em', textTransform: 'uppercase', color: BLUE,
          }}>
            {opportunity.supplier_name}
          </p>
          <h2 style={{
            margin: '6px 0 12px', fontFamily: FONT_STACK, fontWeight: 900,
            fontSize: 28, letterSpacing: '-0.02em', lineHeight: 1.08, color: TEXT,
          }}>
            {opportunity.title}
          </h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: 14 }}>
            <span style={{ color: TEXT, fontFamily: FONT_STACK, fontSize: 14, fontWeight: 700 }}>
              {opportunity.event_date}{opportunity.start_time ? ` · ${opportunity.start_time}` : ''}
            </span>
            <span style={{ color: PINK, fontFamily: FONT_STACK, fontSize: 14, fontWeight: 800 }}>
              {opportunity.pay}
            </span>
            <span style={{ color: TEXT_MUTED, fontFamily: FONT_STACK, fontSize: 13 }}>
              {opportunity.location}
            </span>
          </div>

          {/* Helping with chips */}
          {(opportunity.helping_with || []).length > 0 && (
            <>
              <p style={{
                margin: '0 0 6px', fontFamily: FONT_STACK, fontSize: 11,
                letterSpacing: '0.1em', textTransform: 'uppercase', color: TEXT_MUTED, fontWeight: 700,
              }}>
                You'll be helping with
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                {opportunity.helping_with.slice(0, 3).map((h, i) => (
                  <span key={i} style={{
                    fontFamily: FONT_STACK, fontSize: 12, padding: '5px 12px',
                    borderRadius: 999, background: PANEL, color: TEXT,
                    border: `1px solid ${PANEL_BORDER}`, backdropFilter: 'blur(8px)',
                  }}>
                    {h}
                  </span>
                ))}
              </div>
            </>
          )}

          {/* More info expand */}
          {opportunity.description && (
            <button
              onClick={(e) => { e.stopPropagation(); setShowInfo(!showInfo); }}
              data-testid={`card-more-info-${opportunity.id}`}
              style={{
                background: 'transparent', border: 'none', color: BLUE, cursor: 'pointer',
                padding: 0, fontFamily: FONT_STACK, fontSize: 12, fontWeight: 700,
                display: 'inline-flex', alignItems: 'center', gap: 4,
              }}
            >
              <Info size={12} weight="bold" />
              {showInfo ? 'Less' : 'More info'}
            </button>
          )}
          {showInfo && (
            <p style={{
              marginTop: 10, fontFamily: FONT_STACK, fontSize: 13, color: TEXT,
              lineHeight: 1.55, maxHeight: 140, overflowY: 'auto',
            }}>
              {opportunity.description}
            </p>
          )}

          {/* Bring a friend — only when supplier opted in. Tapping
              toggles the apply-together intent which the swipe-right
              gesture honours when it lands. Friendlier than a hidden
              form: group energy is the entire point of this feature. */}
          {allowsBringFriend && isTop && (
            <button
              onClick={(e) => { e.stopPropagation(); onToggleBringFriend && onToggleBringFriend(!bringFriend); }}
              onPointerDown={(e) => e.stopPropagation()}
              data-testid={`card-bring-friend-${opportunity.id}`}
              style={{
                marginTop: 12, padding: '8px 14px', borderRadius: 999, cursor: 'pointer',
                fontFamily: FONT_STACK, fontSize: 12.5, fontWeight: 800,
                color: TEXT,
                background: bringFriend ? `linear-gradient(135deg, ${PURPLE}, ${PINK})` : 'rgba(0,0,0,0.4)',
                border: bringFriend ? 'none' : `1px solid ${PANEL_BORDER}`,
                backdropFilter: 'blur(8px)',
                display: 'inline-flex', alignItems: 'center', gap: 6,
                boxShadow: bringFriend ? `0 0 16px ${PURPLE}55` : 'none',
              }}
            >
              <UserPlus size={13} weight="fill" />
              {bringFriend ? 'Applying together ✦' : 'Apply together?'}
            </button>
          )}

          {/* Directed-invite ✦ — 3 special action buttons. Sits
              inside the card so the cue stays welded to the
              opportunity context, never floats above the global
              skip/like swipe controls. Emotional contract: "you
              were remembered" — gold, soft pulse, supplier name. */}
          {directed && isTop && (
            <div
              data-testid={`directed-actions-${opportunity.id}`}
              onPointerDown={(e) => e.stopPropagation()}
              style={{
                marginTop: 14, padding: 12, borderRadius: 16,
                background: `linear-gradient(135deg, rgba(255,209,102,0.10), rgba(108,59,255,0.10))`,
                border: `1px solid ${GOLD}66`,
                boxShadow: `0 8px 24px ${GOLD}33, inset 0 0 12px rgba(255,209,102,0.08)`,
              }}
            >
              <p style={{
                margin: '0 0 8px', fontFamily: FONT_STACK, fontSize: 12.5,
                color: TEXT, fontWeight: 700, lineHeight: 1.45,
              }}>
                {(directed.supplier_name || 'A supplier').split(' ')[0]} wants you back for this ✦
                {directed.message && (
                  <span style={{ display: 'block', color: TEXT_MUTED, fontSize: 11.5, fontStyle: 'italic', marginTop: 4 }}>
                    "{directed.message}"
                  </span>
                )}
                <span style={{ display: 'block', color: GOLD, fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', marginTop: 4 }}>
                  YOU MADE A GREAT IMPRESSION ✦
                </span>
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                <button
                  onClick={(e) => { e.stopPropagation(); respondDirected('accepted'); }}
                  disabled={directedBusy === 'accepted'}
                  data-testid={`directed-accept-${opportunity.id}`}
                  style={{
                    padding: '10px 16px', borderRadius: 999, border: 'none',
                    cursor: directedBusy === 'accepted' ? 'wait' : 'pointer',
                    fontFamily: FONT_STACK, fontSize: 12.5, fontWeight: 900,
                    color: '#1A1100',
                    background: `linear-gradient(135deg, ${GOLD}, #FFB347)`,
                    boxShadow: `0 8px 22px ${GOLD}55`,
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                  }}
                >
                  Accept ✦
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); respondDirected('maybe'); }}
                  disabled={directedBusy === 'maybe'}
                  data-testid={`directed-maybe-${opportunity.id}`}
                  style={{
                    padding: '10px 16px', borderRadius: 999,
                    cursor: directedBusy === 'maybe' ? 'wait' : 'pointer',
                    fontFamily: FONT_STACK, fontSize: 12.5, fontWeight: 800,
                    color: TEXT,
                    background: 'rgba(255,255,255,0.08)',
                    border: `1px solid ${PANEL_BORDER}`,
                  }}
                >
                  Maybe Later
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); respondDirected('declined'); }}
                  disabled={directedBusy === 'declined'}
                  data-testid={`directed-decline-${opportunity.id}`}
                  style={{
                    padding: '10px 16px', borderRadius: 999,
                    cursor: directedBusy === 'declined' ? 'wait' : 'pointer',
                    fontFamily: FONT_STACK, fontSize: 12, fontWeight: 700,
                    color: TEXT_MUTED,
                    background: 'transparent',
                    border: `1px solid ${PANEL_BORDER}`,
                  }}
                >
                  Decline
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const ActionButton = ({ onClick, color, glow, children, testId, size = 64 }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    style={{
      width: size, height: size, borderRadius: '50%',
      background: glow
        ? `linear-gradient(135deg, ${color}, ${color}cc)`
        : `${PANEL}`,
      border: glow ? 'none' : `1px solid ${color}88`,
      color: glow ? TEXT : color,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      cursor: 'pointer',
      boxShadow: glow ? `0 8px 28px ${color}66, 0 0 0 1px rgba(255,255,255,0.08) inset` : 'none',
      transition: 'transform 0.15s ease',
    }}
    onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.92)'; }}
    onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
    onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
  >
    {children}
  </button>
);

const CrewFeedPage = () => {
  const navigate = useNavigate();
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [beginnerOnly, setBeginnerOnly] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  // bring-a-friend intent — keyed per opportunity id. The swipe-
  // right gesture pulls this from the top card and includes it in
  // the apply payload so the backend records the linked applicants.
  const [bringFriendByOpp, setBringFriendByOpp] = useState({});
  // Push opt-in fires after the first swipe of the session — never
  // on page load. We just flip the trigger; the banner decides
  // whether to render based on browser + localStorage state.
  const [pushTrigger, setPushTrigger] = useState(false);

  // Reads ?focus= every refresh — leaves the URL intact so React
  // StrictMode (which double-mounts in dev) keeps reordering on
  // remount. Cleared via _clearFocusParam() on the first real user
  // action (swipe / save) so subsequent loads don't re-pin it.
  const _readFocus = () => {
    try {
      return new URLSearchParams(window.location.search).get('focus') || '';
    } catch { return ''; }
  };

  const _clearFocusParam = () => {
    try {
      const url = new URL(window.location.href);
      if (!url.searchParams.get('focus')) return;
      url.searchParams.delete('focus');
      window.history.replaceState({}, '', url.toString());
    } catch { /* ignore */ }
  };

  const refreshFeed = async () => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${API}/crew/feed`, {
        params: beginnerOnly ? { beginner_friendly: 1 } : {},
      });
      let opps = r.data?.opportunities || [];
      const focus = _readFocus();
      if (focus) {
        const i = opps.findIndex((o) => o.id === focus);
        if (i > 0) {
          const next = [...opps];
          const [chosen] = next.splice(i, 1);
          opps = [chosen, ...next];
        }
      }
      setFeed(opps);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not load opportunities');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refreshFeed(); /* eslint-disable-line */ }, [beginnerOnly]);

  const top = feed[0];
  const visibleStack = useMemo(() => feed.slice(0, 3), [feed]);

  const flashToast = (kind, label) => {
    setToast({ kind, label });
    setTimeout(() => setToast(null), 1400);
  };

  const handleSwipe = async (direction, opportunityId) => {
    const oppId = opportunityId || top?.id;
    if (!oppId) return;
    setPushTrigger(true); // first swipe arms the opt-in banner
    _clearFocusParam();   // first real action consumes ?focus=
    // Optimistic pop so the deck feels alive
    setFeed((f) => f.filter((o) => o.id !== oppId));
    try {
      if (direction === 'interested') {
        const bring = Boolean(bringFriendByOpp[oppId]);
        await axios.post(`${API}/crew/opportunities/${oppId}/interested`, {
          bring_friend: bring,
        });
        flashToast('like', bring ? "You're in — together ❤️" : "You're in ❤️");
      } else {
        await axios.post(`${API}/crew/opportunities/${oppId}/skip`, {});
        flashToast('skip', 'Skipped');
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate('/login?next=/crew/feed');
        return;
      }
      setError(err?.response?.data?.detail || 'Action failed');
    }
  };

  const handleSave = async (opportunityId, nextSaved) => {
    try {
      if (nextSaved) {
        await axios.post(`${API}/crew/opportunities/${opportunityId}/save`, {});
        flashToast('like', 'Saved for later ✦');
      } else {
        await axios.delete(`${API}/crew/opportunities/${opportunityId}/save`);
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate('/login?next=/crew/feed');
      }
    }
  };

  // Directed-invite response — Accept ✦ / Maybe Later / Decline.
  // Accept also auto-creates the application server-side, so we
  // pop the card from the deck like a normal interested swipe.
  // "Maybe" keeps the card pinned but logs the soft signal.
  // Decline pops the card and removes the gold treatment.
  const handleRespondDirected = async (inviteId, response, opportunityId) => {
    try {
      await axios.post(`${API}/crew/directed-invites/${inviteId}/respond`, { response });
      if (response === 'accepted') {
        setFeed((f) => f.filter((o) => o.id !== opportunityId));
        flashToast('like', "You're in — they'll love that ❤️");
      } else if (response === 'declined') {
        setFeed((f) => f.filter((o) => o.id !== opportunityId));
        flashToast('skip', 'Declined');
      } else if (response === 'maybe') {
        // Soft signal: drop the directed_invite block entirely so
        // the gold treatment unmounts client-side. The opportunity
        // stays in the feed at its naturally-ranked position; the
        // supplier still sees a `maybe` status server-side.
        setFeed((f) => f.map((o) =>
          o.id === opportunityId ? { ...o, directed_invite: null } : o,
        ));
        flashToast('like', 'Saved for later ✦');
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate('/login?next=/crew/feed');
        return;
      }
      setError(err?.response?.data?.detail || 'Could not save response');
    }
  };

  return (
    <div
      data-testid="crew-feed-page"
      style={{
        minHeight: '100vh', background: BG, color: TEXT,
        fontFamily: FONT_STACK, display: 'flex', flexDirection: 'column',
      }}
    >
      {/* Inline keyframes for the urgency pulse */}
      <style>{`
        @keyframes crewPulse {
          0%, 100% { box-shadow: 0 0 14px rgba(255,60,172,0.45), 0 0 0 1px rgba(255,60,172,0.6) inset; }
          50%      { box-shadow: 0 0 28px rgba(255,60,172,0.85), 0 0 0 1px rgba(255,60,172,1) inset; }
        }
      `}</style>

      {/* Header */}
      <div style={{
        padding: '20px clamp(16px, 5vw, 32px)', display: 'flex',
        alignItems: 'center', justifyContent: 'space-between', gap: 12,
      }}>
        <button
          onClick={() => navigate('/crew')}
          data-testid="feed-back"
          style={{
            background: 'transparent', border: 'none', color: TEXT_MUTED, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: FONT_STACK, fontSize: 13,
          }}
        >
          <ArrowLeft size={14} weight="bold" /> Crew
        </button>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <button
            onClick={() => navigate('/crew/messages')}
            data-testid="feed-link-messages"
            style={{
              background: 'transparent', border: 'none', color: TEXT_MUTED, cursor: 'pointer',
              fontFamily: FONT_STACK, fontSize: 13, fontWeight: 600,
              display: 'inline-flex', alignItems: 'center', gap: 5,
            }}
          >
            <ChatCircleDots size={13} weight="bold" /> Messages
          </button>
          <button
            onClick={() => navigate('/crew/trust')}
            data-testid="feed-link-trust"
            style={{
              background: 'transparent', border: 'none', color: BLUE, cursor: 'pointer',
              fontFamily: FONT_STACK, fontSize: 13, fontWeight: 700,
              display: 'inline-flex', alignItems: 'center', gap: 5,
            }}
          >
            ✦ Trust
          </button>
          <button
            onClick={() => navigate('/crew/saved')}
            data-testid="feed-link-saved"
            style={{
              background: 'transparent', border: 'none', color: TEXT_MUTED, cursor: 'pointer',
              fontFamily: FONT_STACK, fontSize: 13, fontWeight: 600,
              display: 'inline-flex', alignItems: 'center', gap: 5,
            }}
          >
            <BookmarkSimple size={13} weight="bold" /> Saved
          </button>
          <button
            onClick={() => navigate('/crew/profile')}
            data-testid="feed-link-profile"
            style={{
              background: 'transparent', border: 'none', color: TEXT_MUTED, cursor: 'pointer',
              fontFamily: FONT_STACK, fontSize: 13, fontWeight: 600,
            }}
          >
            Profile
          </button>
        </div>
      </div>

      {/* Deck */}
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: 'clamp(8px, 3vw, 24px)',
      }}>
        {/* Filter chip — single pill, beginner-friendly toggle */}
        <button
          onClick={() => setBeginnerOnly((b) => !b)}
          data-testid="feed-filter-beginner"
          style={{
            marginBottom: 12,
            padding: '6px 14px', borderRadius: 999, cursor: 'pointer',
            fontFamily: FONT_STACK, fontSize: 12, fontWeight: 700,
            color: beginnerOnly ? TEXT : TEXT_MUTED,
            background: beginnerOnly ? `linear-gradient(135deg, ${BLUE}, ${PURPLE})` : PANEL,
            border: beginnerOnly ? 'none' : `1px solid ${PANEL_BORDER}`,
          }}
        >
          {beginnerOnly ? '✦ Beginner-friendly only' : 'All levels'}
        </button>
        <div style={{
          position: 'relative', width: '100%', maxWidth: 420,
          height: 'min(72vh, 660px)', flex: '0 0 auto',
        }}>
          {loading ? (
            <p style={{ color: TEXT_MUTED, textAlign: 'center', fontFamily: FONT_STACK }}>Loading feed…</p>
          ) : visibleStack.length === 0 ? (
            <div
              data-testid="feed-empty"
              style={{
                height: '100%', display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', textAlign: 'center',
                padding: 24, borderRadius: 28,
                background: PANEL, border: `1px solid ${PANEL_BORDER}`,
              }}
            >
              <Lightning size={32} weight="fill" color={PINK} />
              <p style={{ margin: '14px 0 8px', fontFamily: FONT_STACK, fontWeight: 900, fontSize: 22 }}>
                You're caught up ✦
              </p>
              <p style={{ margin: 0, color: TEXT_MUTED, fontFamily: FONT_STACK, fontSize: 14, lineHeight: 1.5 }}>
                Check back tomorrow — new opportunities drop daily.
              </p>
              <button
                onClick={refreshFeed}
                data-testid="feed-refresh"
                style={{
                  marginTop: 22, padding: '12px 24px', borderRadius: 999,
                  border: 'none', cursor: 'pointer', color: TEXT, fontWeight: 800,
                  fontFamily: FONT_STACK, fontSize: 14,
                  background: `linear-gradient(135deg, ${PURPLE}, ${PINK})`,
                }}
              >
                Refresh
              </button>
            </div>
          ) : (
            <AnimatePresence>
              {visibleStack.map((opp, i) => (
                <OpportunityCard
                  key={opp.id}
                  opportunity={opp}
                  isTop={i === 0}
                  stackIndex={i}
                  bringFriend={Boolean(bringFriendByOpp[opp.id])}
                  onToggleBringFriend={(next) =>
                    setBringFriendByOpp((m) => ({ ...m, [opp.id]: next }))
                  }
                  onSave={handleSave}
                  onSwipe={(dir) => handleSwipe(dir, opp.id)}
                  onRespondDirected={handleRespondDirected}
                />
              ))}
            </AnimatePresence>
          )}
        </div>
      </div>

      {/* Action bar */}
      {visibleStack.length > 0 && (
        <div style={{
          padding: '20px clamp(16px, 5vw, 32px) 36px',
          display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 22,
        }}>
          <ActionButton color={TEXT_MUTED} onClick={() => handleSwipe('skip')} testId="action-skip">
            <XIcon size={28} weight="bold" />
          </ActionButton>
          <ActionButton color={BLUE} size={52} onClick={() => { /* card has its own toggle */ }} testId="action-more-info">
            <Info size={22} weight="bold" />
          </ActionButton>
          <ActionButton color={PINK} glow onClick={() => handleSwipe('interested')} testId="action-interested">
            <Heart size={28} weight="fill" />
          </ActionButton>
        </div>
      )}

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            data-testid="feed-toast"
            style={{
              position: 'fixed', bottom: 110, left: '50%', transform: 'translateX(-50%)',
              padding: '12px 22px', borderRadius: 999,
              background: toast.kind === 'like'
                ? `linear-gradient(135deg, ${PINK}, ${PURPLE})`
                : PANEL,
              border: toast.kind === 'like' ? 'none' : `1px solid ${PANEL_BORDER}`,
              color: TEXT, fontFamily: FONT_STACK, fontWeight: 800, fontSize: 14,
              boxShadow: toast.kind === 'like' ? `0 8px 28px ${PINK}66` : '0 8px 22px rgba(0,0,0,0.4)',
              zIndex: 50,
            }}
          >
            {toast.kind === 'like' ? <Heart size={14} weight="fill" style={{ display: 'inline', marginRight: 6 }} /> : null}
            {toast.label}
          </motion.div>
        )}
      </AnimatePresence>

      {error && (
        <p data-testid="feed-error" style={{
          position: 'fixed', bottom: 24, left: 24, right: 24,
          textAlign: 'center', color: PINK, fontSize: 13, fontFamily: FONT_STACK,
        }}>{error}</p>
      )}

      <CrewPushOptIn trigger={pushTrigger} source="feed" />
    </div>
  );
};

export default CrewFeedPage;
